import { computed, effect, inject, signal, untracked } from '@angular/core';
import { httpResource } from '@angular/common/http';
import { form, maxLength, validate } from '@angular/forms/signals';
import type { FieldState, PathKind, SchemaPath, SchemaPathRules } from '@angular/forms/signals';
import { SchoolMember, SchoolMessageData, SchoolMessageResult } from '../models/school-user.model';
import { AuthService } from '../../auth/services/auth.service';
import { ApiErrorHandler } from '../../core/errors/api-error-handler';
import { ApiErrorMessages } from '../../core/errors/api-error';
import { ApiErrorCode } from '../../core/errors/api-error-code';
import { environment } from '../../../environments/environment';

export const SCHOOL_MESSAGE_SUBJECT_MAX = 200;
export const SCHOOL_MESSAGE_BODY_MAX = 5000;
export const SCHOOL_MESSAGE_RECIPIENTS_MAX = 300;

/** The server's subject pattern: ASCII control characters plus the line and paragraph separators. */
function hasForbiddenSubjectChar(subject: string): boolean {
  return [...subject].some((ch) => {
    const code = ch.codePointAt(0)!;
    return code <= 0x1f || code === 0x7f || code === 0x2028 || code === 0x2029;
  });
}

/** The server's limits on a message's subject and body, shared by every e-mail a user writes here. */
export function messageTextRules<TPathKind extends PathKind = PathKind.Root>(
  subject: SchemaPath<string, SchemaPathRules.Supported, TPathKind>,
  body: SchemaPath<string, SchemaPathRules.Supported, TPathKind>,
): void {
  validate(subject, ({ value }) => {
    if (value().trim().length === 0) return { kind: 'required', message: 'Předmět je prázdný' };
    return hasForbiddenSubjectChar(value())
      ? { kind: 'pattern', message: 'Předmět nesmí obsahovat tabulátor ani jiné řídicí znaky' }
      : null;
  });
  maxLength(subject, SCHOOL_MESSAGE_SUBJECT_MAX, {
    message: `Předmět může mít maximálně ${SCHOOL_MESSAGE_SUBJECT_MAX} znaků`,
  });
  validate(body, ({ value }) =>
    value().trim().length === 0 ? { kind: 'required', message: 'Text zprávy je prázdný' } : null,
  );
  maxLength(body, SCHOOL_MESSAGE_BODY_MAX, {
    message: `Zpráva může mít maximálně ${SCHOOL_MESSAGE_BODY_MAX} znaků`,
  });
}

/** The "select all" button of a multi-select; an empty list has nothing to select, so it is never all. */
export function selectAllToggle(field: () => FieldState<string[]>, ids: () => string[]) {
  const allSelected = computed(() => {
    const all = ids();
    const selected = new Set(field().value());
    return all.length > 0 && all.every((id) => selected.has(id));
  });
  return {
    allSelected,
    toggleAll(): void {
      field().value.set(allSelected() ? [] : ids());
      field().markAsTouched();
    },
  };
}

/** The composer reloads the member list on this code, so the server's "reload it" is already done. */
export const SCHOOL_MESSAGE_ERRORS: ApiErrorMessages = {
  SCHOOL_MESSAGE_RECIPIENT_NOT_MEMBER:
    'Někdo z vybraných příjemců už není členem školy. Seznam jsme obnovili — zkontrolujte příjemce a odešlete zprávu znovu.',
};

export type SchoolMessageComposer = ReturnType<typeof schoolMessageComposer>;

/**
 * The state of a message to colleagues — recipients, subject, body and their validation — shared by
 * the dialog on the members page and the floating chat, which render it with `SchoolMessageForm`
 * and send it each their own way. Call it in a field initializer, like `inject()`.
 *
 * The member list is fetched here rather than read from `SchoolUserService`: the floating chat
 * writes to the header's school, which need not be the school a members page underneath shows.
 */
export function schoolMessageComposer(
  schoolId: () => string | null,
  initialRecipientIds: string[] = [],
) {
  const authService = inject(AuthService);
  const apiErrors = inject(ApiErrorHandler);

  const membersResource = httpResource<SchoolMember[]>(() => {
    const id = schoolId();
    return id ? `${environment.apiUrl}/schools/${id}/users` : undefined;
  });

  const recipients = computed(() => {
    const members = membersResource.hasValue() ? membersResource.value() : [];
    const email = authService.currentUser()?.email?.toLowerCase();
    return members.filter((m) => m.email.toLowerCase() !== email);
  });

  const model = signal<SchoolMessageData>({
    recipientIds: initialRecipientIds,
    subject: '',
    body: '',
  });

  const messageForm = form(model, (path) => {
    validate(path.recipientIds, ({ value }) =>
      value().length === 0
        ? { kind: 'required', message: 'Vyberte alespoň jednoho příjemce' }
        : value().length > SCHOOL_MESSAGE_RECIPIENTS_MAX
          ? {
              kind: 'maxLength',
              message: `Najednou lze napsat nejvýše ${SCHOOL_MESSAGE_RECIPIENTS_MAX} příjemcům`,
            }
          : null,
    );
    messageTextRules(path.subject, path.body);
  });

  effect(() => {
    if (membersResource.status() === 'error') {
      apiErrors.notify(membersResource.error(), 'SchoolMessageComposer.loadMembers');
    }
  });

  // A picked recipient who is not on the loaded list — the header switched schools under an open
  // floating chat — would be refused by the server; drop them where the user can see it.
  effect(() => {
    if (!membersResource.hasValue()) return;
    const known = new Set(recipients().map((m) => m.id));
    const picked = untracked(model).recipientIds;
    const kept = picked.filter((id) => known.has(id));
    if (kept.length !== picked.length) {
      model.update((m) => ({ ...m, recipientIds: kept }));
    }
  });

  /** Who did not get the last message, named in the form until the next send. */
  const undelivered = signal<SchoolMember[]>([]);

  const selectAll = selectAllToggle(
    () => messageForm.recipientIds(),
    () => recipients().map((m) => m.id),
  );

  return {
    model: model.asReadonly(),
    form: messageForm,
    recipients,
    membersLoading: membersResource.isLoading,
    allSelected: selectAll.allSelected,
    toggleAll: selectAll.toggleAll,
    undelivered: undelivered.asReadonly(),

    /** Marks every field touched so its error shows; `true` when the message may be sent. */
    validate(): boolean {
      messageForm().markAsTouched();
      return messageForm().valid();
    },

    reset(): void {
      messageForm().reset({ recipientIds: [], subject: '', body: '' });
      undelivered.set([]);
    },

    /**
     * Takes a send's result in: `true` when everyone got the message. Otherwise the text stays and
     * the recipients narrow to those who missed it, so sending again cannot duplicate it for the rest.
     */
    settle(result: SchoolMessageResult): boolean {
      const missed = new Set(result.undeliveredRecipientIds);
      undelivered.set(recipients().filter((m) => missed.has(m.id)));
      if (missed.size === 0) return true;
      messageForm.recipientIds().value.set([...missed]);
      return false;
    },

    /** A recipient has left the school since the list loaded — fetch it again; the stale pick drops out. */
    handleSendFailure(code: string | undefined): void {
      if (code === ApiErrorCode.SCHOOL_MESSAGE_RECIPIENT_NOT_MEMBER) {
        membersResource.reload();
      }
    },
  };
}
